import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Dropdown,
  Button,
  Popover,
  Empty,
  Typography,
  Spin,
  Select,
  Modal,
  Toast,
} from "@douyinfe/semi-ui";
import {
  AntibodyDesignBinderTypeEnum,
  JobStatusEnum,
  JobTypeEnum,
  PeptideDesignBinderTypeEnum,
} from "@services/home/enum/home.enum";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { setIsJobSubmited, setLoginDialogVisible } from "@state/global/reducer";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@state/store";
import {
  IllustrationNoAccessDark,
  IllustrationNoAccess,
  IllustrationNoContent,
  IllustrationNoContentDark,
} from "@douyinfe/semi-illustrations";
import { IconChevronRight } from "@douyinfe/semi-icons";
import {
  HistoryGroupModel,
  JobHistoryModel,
  PeptideDesignPayloadModel,
  VhhDesignPayloadModel,
} from "@services/home/model/home.model";
import { deleteJobApi } from "@services/task/task.api";
import { getUserInfoApi } from "@services/user/user.api";
import { JOB_TYPE_LIST } from "@services/home/config/home.config";
import LoginDialog from "../login-dialog";
import { ReactComponent as StructIcon } from "@assets/icons/struct_icon.svg";
import { ReactComponent as ArrowIcon } from "@assets/icons/arrow_icon.svg";
import { ReactComponent as MenuIcon } from "@assets/icons/menu_icon.svg";
import { ReactComponent as ErrorIcon } from "@assets/icons/error_icon.svg";
import { getJobListApi, getJobDetailApi } from "@services/home/home.api";
import { groupJobHistory } from "@utils/data-process.util";
import { eventTarget } from "@utils/event-target.util";
import JobOptButton from "../job-opt-button";
import RenameJobnameDialog from "../rename-job-name-dialog";

import styles from "./index.module.scss";
import classNames from "classnames";

const { Text } = Typography;

const HoverGadget = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [userType, setUserType] = useState("");
  const [groupList, setGroupList] = useState<HistoryGroupModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
  const [renameDialogVisible, setRenameDialogVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobHistoryModel>();
  const [selectedType, setSelectedType] = useState(
    JobTypeEnum.ProteinBinderDesign
  );
  const historyVisibleRef = useRef(false);

  const selectedMenu = useSelector(
    (state: RootState) => state.global.selectedMenu
  );

  const isJobSubmited = useSelector(
    (state: RootState) => state.global.isJobSubmited
  );

  const loginDialogVisible = useSelector(
    (state: RootState) => state.global.loginDialogVisible
  );

  function toHome(type: string) {
    navigate(`/home?job_type=${type}`);
  }

  const getPepideLabel = (type: PeptideDesignBinderTypeEnum): string =>
    t(`peptideDesignType.${type}`);

  const getJobLabel = (type: JobTypeEnum): string => t(`jobType.${type}`);

  const antibodySubDropdown = useMemo(
    () => (
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={() =>
            toHome(
              `${JobTypeEnum.VhhDesign}-${AntibodyDesignBinderTypeEnum.Vhh}`
            )
          }
        >
          VHH
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() =>
            toHome(
              `${JobTypeEnum.VhhDesign}-${AntibodyDesignBinderTypeEnum.Scfv}`
            )
          }
        >
          IgG
        </Dropdown.Item>
      </Dropdown.Menu>
    ),
    []
  );

  const peptideSubDropdown = useMemo(
    () => (
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={() =>
            toHome(
              `${JobTypeEnum.PeptideDesignLite}-${PeptideDesignBinderTypeEnum.LinPep}`
            )
          }
        >
          {getPepideLabel(PeptideDesignBinderTypeEnum.LinPep)}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() =>
            toHome(
              `${JobTypeEnum.PeptideDesignLite}-${PeptideDesignBinderTypeEnum.DisulPep}`
            )
          }
        >
          {getPepideLabel(PeptideDesignBinderTypeEnum.DisulPep)}
        </Dropdown.Item>
        <Dropdown.Item
          onClick={() =>
            toHome(
              `${JobTypeEnum.PeptideDesignLite}-${PeptideDesignBinderTypeEnum.CycPep}`
            )
          }
        >
          {getPepideLabel(PeptideDesignBinderTypeEnum.CycPep)}
        </Dropdown.Item>
      </Dropdown.Menu>
    ),
    [i18n.resolvedLanguage]
  );

  function cancelDialog() {
    dispatch(setLoginDialogVisible(false));
  }

  function onLoginSubmit() {
    cancelDialog();

    eventTarget.dispatchEvent(new CustomEvent("login-success"));
  }

  async function loadData(type_name: JobTypeEnum) {
    try {
      const data = await getJobListApi({
        page: 1,
        page_size: 20000,
        type_name,
      });

      const groups = groupJobHistory(data.list);
      setGroupList(groups);
    } catch (e) {
      console.error(e);
    }
  }

  function onCloseHistory() {
    if (document.querySelector(".semi-modal-mask")) {
      return;
    }
    setTimeout(() => {
      setHistoryDialogVisible(false);
      historyVisibleRef.current = false;
    }, 100);
  }

  function openDialog() {
    dispatch(setLoginDialogVisible(true));
    onCloseHistory();
  }

  function onHistoryClick() {
    if (!historyVisibleRef.current) {
      historyVisibleRef.current = true;
      setHistoryDialogVisible(true);
      setIsLoading(true);
      getUserInfoApi()
        .then(({ code }) => {
          if (code === 200) {
            setUserType("registered");
            loadData(selectedType)
              .then(() => {
                setIsLoading(false);
              })
              .catch(() => {
                setIsLoading(false);
              });
          } else {
            setIsLoading(false);
            setUserType("anonymous");
          }
        })
        .catch(() => {
          setIsLoading(false);
          setUserType("anonymous");
        });
    }
  }

  function onRename(item: JobHistoryModel) {
    setSelectedJob(item);
    setRenameDialogVisible(true);
    // setTimeout(() => {
    //   setHistoryDialogVisible(true);
    // }, 100);
  }

  async function getJobDetail(id: string) {
    const data = await getJobDetailApi(id);
    const jobType = data.type_name;

    if (jobType === JobTypeEnum.VhhDesign) {
      const params = data.parameters as VhhDesignPayloadModel;
      navigate(
        `/job?job_id=${data.id}&job_type=${jobType}-${params.binder_type}`
      );
    } else if (jobType === JobTypeEnum.PeptideDesignLite) {
      const params = data.parameters as PeptideDesignPayloadModel;
      navigate(
        `/peptide-job?job_id=${data.id}&job_type=${jobType}-${params.binder_type}`
      );
    } else if (jobType === JobTypeEnum.ProteinBinderDesign) {
      Toast.info(t("gadget.message.binderDesignNotSupport"));
    } else {
      navigate(`/home?job_id=${data.id}`);
    }
  }

  function onRerun(item: JobHistoryModel) {
    getJobDetail(item.id);
  }

  function onRefresh() {
    loadData(selectedType).catch(() => {});
  }

  function onDelete(item: JobHistoryModel) {
    // setTimeout(() => {
    //   setHistoryDialogVisible(true);
    // }, 100);

    const model = Modal.warning({
      zIndex: 9999,
      title: t("gadget.deleteModal.title"),
      content: t("gadget.deleteModal.content"),
      onOk: async () => {
        const { code, message } = await deleteJobApi([item.id]);
        if (code === 200) {
          model.destroy();
          Toast.success(message);
          onRefresh();
        }
      },
      okText: t("gadget.deleteModal.okText"),
      cancelText: t("gadget.deleteModal.cancelText"),
    });
  }

  function renameDialogCancel() {
    setRenameDialogVisible(false);
    setSelectedJob(undefined);
  }

  function renameDialogSubmit() {
    renameDialogCancel();
    onRefresh();
  }

  function toDetail(item: JobHistoryModel) {
    navigate(`/job-detail/${item.id}`);
  }

  function onTypeChange(
    type: string | number | any[] | Record<string, any> | undefined
  ) {
    if (typeof type !== "string") {
      return;
    }

    setSelectedType(type as JobTypeEnum);

    if (userType === "registered") {
      loadData(type as JobTypeEnum);
    }
  }

  function onCloseHint() {
    dispatch(setIsJobSubmited(false));
  }

  useEffect(() => {
    if (selectedMenu) {
      setSelectedType(selectedMenu.job_type);
    }
  }, [selectedMenu]);

  return (
    <div className={styles.container}>
      <div>
        <Dropdown
          position="leftBottom"
          style={{ width: "244px" }}
          clickToHide
          render={
            <Dropdown.Menu>
              <Dropdown.Title>{t("gadget.newJob")}</Dropdown.Title>
              {JOB_TYPE_LIST.filter((item) => {
                return item.value !== JobTypeEnum.ProteinBinderDesign;
              }).map((item) => {
                if (item.value === JobTypeEnum.VhhDesign) {
                  return (
                    <Dropdown
                      position={"leftTop"}
                      render={antibodySubDropdown}
                      key={item.value}
                    >
                      <Dropdown.Item
                        className={styles.dropdownItem}
                        key={item.value}
                      >
                        <span>{getJobLabel(item.value)}</span>

                        <IconChevronRight
                          style={{
                            marginRight: 0,
                            fontSize: "16px",
                            color: "var(--semi-color-text-2)",
                          }}
                        />
                      </Dropdown.Item>
                    </Dropdown>
                  );
                }

                if (item.value === JobTypeEnum.PeptideDesignLite) {
                  return (
                    <Dropdown
                      position={"leftTop"}
                      render={peptideSubDropdown}
                      key={item.value}
                    >
                      <Dropdown.Item
                        className={styles.dropdownItem}
                        key={item.value}
                      >
                        <span>{getJobLabel(item.value)}</span>
                        <IconChevronRight
                          style={{
                            marginRight: 0,
                            fontSize: "16px",
                            color: "var(--semi-color-text-2)",
                          }}
                        />
                      </Dropdown.Item>
                    </Dropdown>
                  );
                }

                return (
                  <Dropdown.Item
                    className={styles.dropdownItem}
                    key={item.value}
                    onClick={() => toHome(item.value)}
                  >
                    {getJobLabel(item.value as JobTypeEnum)}
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          }
        >
          <div className={styles.iconWrapper}>
            <StructIcon />
          </div>
        </Dropdown>
      </div>
      <div>
        <Popover
          onClickOutSide={onCloseHistory}
          style={{ width: "320px" }}
          position="left"
          trigger="custom"
          visible={historyDialogVisible}
          content={
            <div className={styles.historyContainer}>
              {isLoading && (
                <div className={styles.loadingContainer}>
                  <Spin size="large" />
                </div>
              )}

              {!isLoading && (
                <>
                  <div className={styles.titleContainer}>
                    <div className={styles.title}>{t("gadget.jobHistory")}</div>
                    <Select
                      size="small"
                      value={selectedType}
                      onChange={onTypeChange}
                      className={styles.select}
                    >
                      {JOB_TYPE_LIST.map((item) => {
                        return (
                          <Select.Option
                            value={item.value}
                            key={item.value}
                            showTick={false}
                            className={styles.option}
                          >
                            {getJobLabel(item.value as JobTypeEnum)}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                  <Dropdown.Menu className={styles.dropdownContent}>
                    {userType === "registered" ? (
                      <>
                        {groupList.length > 0 ? (
                          <>
                            {groupList.map((group) => {
                              return (
                                <React.Fragment key={group.group_name}>
                                  <Dropdown.Title>
                                    {group.group_name}
                                  </Dropdown.Title>
                                  {group.list.map((item) => {
                                    return (
                                      <Dropdown.Item
                                        key={item.id}
                                        className={classNames({
                                          [styles.jobRow]: true,
                                          [styles.active]:
                                            selectedMenu?.job_id === item.id,
                                        })}
                                      >
                                        <Text
                                          ellipsis={{ showTooltip: true }}
                                          style={{ width: "calc(100% - 24px)" }}
                                          onClick={() => toDetail(item)}
                                        >
                                          {item.name}
                                        </Text>
                                        <span className={styles.operateWrapper}>
                                          <JobOptButton
                                            job_type={selectedType}
                                            jobItem={item}
                                            rename={onRename}
                                            refresh={onRefresh}
                                            rerun={onRerun}
                                            deleteItem={onDelete}
                                          />
                                        </span>
                                        <span className={styles.statusWrapper}>
                                          {item.status ===
                                            JobStatusEnum.Failed && (
                                            <ErrorIcon
                                              className={styles.errorIcon}
                                            />
                                          )}

                                          {(item.status ===
                                            JobStatusEnum.Executing ||
                                            item.status ===
                                              JobStatusEnum.Pending) && (
                                            <Spin
                                              size="small"
                                              wrapperClassName={
                                                styles.loadingIcon
                                              }
                                            />
                                          )}
                                        </span>
                                      </Dropdown.Item>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </>
                        ) : (
                          <div className={styles.noContent}>
                            <Empty
                              image={
                                <IllustrationNoContent
                                  style={{ width: 120, height: 120 }}
                                />
                              }
                              darkModeImage={
                                <IllustrationNoContentDark
                                  style={{ width: 120, height: 120 }}
                                />
                              }
                              description={t("gadget.message.noJob")}
                              style={{
                                width: 250,
                                margin: "0 auto",
                                display: "flex",
                                padding: 20,
                              }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={styles.emptyContainer}>
                        <Empty
                          image={
                            <IllustrationNoAccess
                              style={{ width: 120, height: 120 }}
                            />
                          }
                          darkModeImage={
                            <IllustrationNoAccessDark
                              style={{ width: 120, height: 120 }}
                            />
                          }
                          description={t("gadget.message.notLogin")}
                          style={{
                            width: 244,
                            margin: "0 auto",
                            display: "flex",
                            padding: 20,
                          }}
                        />
                        <div className={styles.btnWrapper}>
                          <Button
                            theme="light"
                            type="tertiary"
                            size="small"
                            onClick={openDialog}
                          >
                            {t("gadget.operation.login")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Dropdown.Menu>
                </>
              )}
            </div>
          }
        >
          <div className={styles.historyBtnWrapper}>
            <Popover
              content={t("gadget.jobHistory")}
              position="leftBottom"
              className={styles.tooltip}
            >
              <div className={styles.iconWrapper} onClick={onHistoryClick}>
                <MenuIcon />
              </div>
            </Popover>

            {isJobSubmited && (
              <div className={styles.noticeDialog}>
                <ArrowIcon className={styles.arrowIcon} />
                <div>{t("gadget.message.submitJobMsg")}</div>
                <div className={styles.btnWrapper}>
                  <span className={styles.btn} onClick={onCloseHint}>
                    {t("gadget.operation.okText")}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Popover>
      </div>

      {loginDialogVisible && (
        <LoginDialog submit={onLoginSubmit} cancel={cancelDialog} />
      )}

      {renameDialogVisible && selectedJob && (
        <RenameJobnameDialog
          jid={selectedJob.id}
          name={selectedJob.name}
          cancel={renameDialogCancel}
          submit={renameDialogSubmit}
        />
      )}

      {isJobSubmited && <div className={styles.mask}></div>}
    </div>
  );
};

export default HoverGadget;
